import {useEffect, useState} from 'react';
import {Form, Select, Table, Spin, Button} from 'antd';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {ModalShell} from './ui/ModalShell';
import theme from '../ordersTheme.module.scss';
import {brokerOptionLabel} from '../brokerVenues';
import {GET_ALL_BROKERS} from 'graphql/queries/stocks';
import {renderTemplateString} from './orderActionsModal';
import styles from './viewOrders.module.scss';

const columns = [
  {
    title: 'Ticker',
    dataIndex: 'ticker',
    key: 'ticker',
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
  },
  {
    title: 'Price',
    dataIndex: 'price',
    key: 'price',
    render: value =>
      value != null ? Number(value).toLocaleString('en-US') : '-',
  },
];

const VIEW_MODES = [
  {key: 'portfolio', label: 'Portfolio'},
  {key: 'cash', label: 'Cash Balance'},
];

const BrokerPortfolioModal = ({open, onClose}) => {
  const [form] = Form.useForm();
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [viewMode, setViewMode] = useState('portfolio');
  const [loading, setLoading] = useState(false);
  const [portfolioData, setPortfolioData] = useState([]);
  const [cashBalance, setCashBalance] = useState(null);

  const {data: brokersData, loading: loadingBrokers} = useQuery(
    GET_ALL_BROKERS,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedBroker(null);
      setPortfolioData([]);
      setCashBalance(null);
      setViewMode('portfolio');
    }
  }, [open]);

  const handleSelection = value => {
    setSelectedBroker(value);
  };

  const fetchAsset = async (url, mode) => {
    if (!url) {
      setPortfolioData([]);
      setCashBalance(null);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (!response.ok) {
        throw new Error('Unable to download broker data');
      }
      const payload = await response.json();
      if (mode === 'portfolio') {
        const rows = Array.isArray(payload)
          ? payload
          : payload?.data || payload?.portfolio || [];
        setPortfolioData(rows);
      } else {
        const cash =
          payload?.cash ??
          payload?.data?.cash ??
          payload?.balance ??
          payload?.cashBalance ??
          null;
        setCashBalance(cash);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedBroker) {
      setPortfolioData([]);
      setCashBalance(null);
      return;
    }
    const broker = brokersData?.getBrokers?.data?.find(
      item => item?.id === selectedBroker,
    );
    if (!broker) {
      setPortfolioData([]);
      setCashBalance(null);
      return;
    }
    const assetKey = viewMode === 'cash' ? 'cashUrl' : 'portfolioUrl';
    const assetUrl = broker?.[assetKey];
    if (!assetUrl) {
      toast.warn(
        viewMode === 'cash'
          ? 'Selected broker does not expose a cash download link.'
          : 'Selected broker does not provide a portfolio download link.',
      );
      setPortfolioData([]);
      setCashBalance(null);
      return;
    }
    const url = renderTemplateString(assetUrl, {
      portfolioId: broker.id,
      cashId: broker.id,
    });
    fetchAsset(url, viewMode);
  }, [selectedBroker, viewMode]);

  const rowsToRender =
    viewMode === 'portfolio'
      ? selectedBroker && portfolioData.length
        ? portfolioData
        : []
      : [];
  const emptyMessage =
    viewMode === 'portfolio'
      ? selectedBroker
        ? 'No entries returned from this broker'
        : 'Select a broker to load their portfolio'
      : 'Select a broker to see their cash balance';

  const handleExportCSV = rows => {
    if (!rows?.length) {
      toast.info('No data to export');
      return;
    }

    const header = ['Ticker', 'Quantity', 'Price'];
    const csvRows = rows.map(row => [
      row.ticker || '',
      row.quantity ?? '',
      row.price ?? '',
    ]);
    const csvContent = [header, ...csvRows]
      .map(cols =>
        cols.map(col => `"${`${col}`.replace(/"/g, '""')}"`).join(','),
      )
      .join('\n');

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `broker_portfolio_${selectedBroker || 'sample'}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCash = () =>
    cashBalance != null
      ? Number(cashBalance).toLocaleString('en-US', {
          style: 'currency',
          currency: 'XOF',
        })
      : 'Select a broker to view cash balance';

  const handleClose = () => {
    form.resetFields();
    setSelectedBroker(null);
    setPortfolioData([]);
    setCashBalance(null);
    setViewMode('portfolio');
    onClose();
  };

  return (
    <DBModal
      isOpen={open}
      handleClose={handleClose}
      width={900}
      destroyOnClose
      content={
        <ModalShell
          title="Broker portfolio & cash balance"
          subtitle="Reads the broker's own portfolio or cash endpoint. A broker with no link configured for the view you pick returns nothing.">
          <div className={theme.brokerBar}>
            <Form
              form={form}
              layout="vertical"
              className={styles.brokerPortfolioForm}>
              <Form.Item label="Select Broker" name="broker">
                <Select
                  placeholder="Select a broker"
                  loading={loadingBrokers}
                  allowClear
                  onChange={handleSelection}>
                  {brokersData?.getBrokers?.data?.map(broker => (
                    <Select.Option key={broker?.id} value={broker?.id}>
                      {brokerOptionLabel(broker)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
            <div className={theme.segmented}>
              {VIEW_MODES.map(mode => (
                <button
                  type="button"
                  key={mode.key}
                  className={`${theme.segment} ${
                    viewMode === mode.key ? theme.segmentActive : ''
                  }`}
                  onClick={() => setViewMode(mode.key)}>
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.brokerPortfolioTable}>
            {loading ? (
              <div className={styles.spinnerWrapper}>
                <Spin />
              </div>
            ) : viewMode === 'portfolio' ? (
              <>
                <Table
                  dataSource={rowsToRender}
                  columns={columns}
                  pagination={{pageSize: 10}}
                  className="antd-table"
                  rowKey={(row, index) => `${row?.ticker || index}`}
                  locale={{emptyText: emptyMessage}}
                />
                {rowsToRender?.length > 0 && (
                  <div className={styles.exportRow}>
                    <Button
                      type="primary"
                      onClick={() => handleExportCSV(rowsToRender)}>
                      Export CSV
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.cashPanel}>
                <p className={styles.cashLabel}>Cash Balance</p>
                <p className={styles.cashValue}>{formatCash()}</p>
              </div>
            )}
          </div>
        </ModalShell>
      }
    />
  );
};

export default BrokerPortfolioModal;
